// src/components/Facilities/animalRowHelpers.js
//
// I-03 R5 · S3-05.1 — the Animals grid's row derivations, at MODULE
// SCOPE.
//
// Every one of these used to be declared inside the JSX, re-created for
// every cell of every row on every render: `getAnimalCommonName` was a
// function DECLARATION inside an IIFE in a table cell, and
// `LISTING_DISPLAY` was an object literal — a twelve-entry table with
// twelve tooltip strings — rebuilt per row. Hoisting them is why the row
// can become a memoised component at all.
//
// The LISTING_DISPLAY table is carried across VERBATIM: every entry, and
// every word of every tooltip. Those sentences are the answer to "why
// isn't this animal live?" and they were written one at a time against
// real operator confusion (Cowork B8, F7, I-02d).
import { rackAndSlot } from '../Racks/naming';
import { sexDisplay } from '../../constants/enums';
import { formatSpeciesName, formatLocality } from '../../utils/scientificName';

/**
 * The COMMON name — the animal's own if it carries one, else the
 * species'. The chain is unchanged; only its location is.
 */
export const getAnimalCommonName = (animal) => {
  if (animal?.common_name) return animal.common_name;
  const sp = animal?.species || {};
  if (sp.common_names_list?.[0]) return sp.common_names_list[0];
  const cn0 = sp.common_names?.[0];
  if (cn0 && typeof cn0 === 'object' && cn0.name) return cn0.name;
  if (sp.common_name) return sp.common_name;
  return 'Unknown';
};

/** 'Correlophus ciliatus' plus a quoted locality when there is one —
 *  the ONE displayed species name (`utils/scientificName`). */
export const scientificName = (animal) => formatSpeciesName(animal?.species || {});

/**
 * What a Where cell reads when the row is in no tank.
 *
 * ⚠️ ONE CONSTANT, BOTH GRIDS (Cowork run 22, finding 10). The Animals
 * grid printed an em dash and the Clutches grid printed the word "None"
 * for the same state, on two pages an operator reads one after the other —
 * and "None" is a word, so it reads like a tank called None. The em dash
 * is what every other missing value on both grids already prints (the Age
 * cell, the Morph cell, `listingDisplay`'s own placeholder).
 */
export const NO_PLACE = '—';

/**
 * The Enclosure cell — "Dart 1 — Rack 2 · 1.3".
 *
 * **THE RACK GOES BESIDE THE SLOT** (operator ruling 2026-09-14, "the
 * rack beside the slot"). A tank's position LABEL is the slot WITHIN a
 * rack, and every rack in the building has a 1.3 — so a grid that shows
 * a tank OUTSIDE its rack has to name the rack next to it. `rackAndSlot`
 * is the ONE implementation of that pairing; nothing here concatenates
 * the separator by hand.
 *
 * ⚠️ **THE SLOT IS NEVER PRINTED WITHOUT ITS RACK.** Half the pair is
 * worse than none of it, because "1.3" on its own reads like an answer
 * — so a payload with no `rack_name` prints no slot at all, and a
 * payload with no slot prints the rack alone, exactly as this cell did
 * before the ruling.
 *
 * The em dash separates the tank from WHERE it is; the ` · ` inside the
 * pair belongs to the rack/slot pairing alone, so the two joins never
 * read as one flat list of three things.
 */
export const enclosureCellText = (animal) => {
  const rackName = animal.rack_name || '';
  const place = rackName
    ? (rackAndSlot({ rackName, positionLabel: animal.enclosure_position_label })
      || rackName)
    : '';
  return `${animal.enclosure_name}${place ? ` — ${place}` : ''}`;
};

/**
 * The Where cell as TWO LINES (Fable ruling 2026-10-06, Design System v1
 * §2.5): line one the tank's name, line two WHERE it is — "Rack 2 · 1.3"
 * through the same `rackAndSlot` pairing `enclosureCellText` uses — each
 * line ellipsised on its own, so the rack and the slot are never the part
 * a narrow column cuts off (one clamped string put them at its end).
 *
 *   racked            → { tank: 'Dart 1', place: 'Rack 2 · 1.3' }
 *   a rack, no slot   → { tank: 'Dart 1', place: 'Rack 2' }
 *   freestanding      → { tank: 'Beardie tank', place: 'freestanding' }
 *   a slot, no rack   → { tank: 'Dart 1', place: '' }   (the slot is never
 *                        printed without its rack — see above)
 *
 * The whole sentence is still `enclosureCellText`, which is the title.
 */
export const FREESTANDING = 'freestanding';

export const placeLines = (tankName, rackName, positionLabel) => {
  const rack = String(rackName || '').trim();
  const label = String(positionLabel || '').trim();
  if (rack) {
    // The HALVES of `rackAndSlot`'s pair, so the slot can be drawn as its
    // own unshrinkable piece: a long rack name ellipsises, the slot never
    // does. `place` is the pair exactly as `rackAndSlot` writes it.
    return {
      tank: tankName,
      place: rackAndSlot({ rackName: rack, positionLabel: label }) || rack,
      rack,
      slot: label,
    };
  }
  return { tank: tankName, place: label ? '' : FREESTANDING, rack: '', slot: '' };
};

export const enclosureCellLines = (animal) => placeLines(
  animal.enclosure_name, animal.rack_name, animal.enclosure_position_label,
);

/**
 * ONE ANIMAL, SAID SO THE KEEPER KNOWS WHICH ONE AND WHERE IT IS
 * (prod hotfix 2026-10-05, the operator: "everything is missing enough
 * data to actually connect things together").
 *
 * The ONE identity of an animal row outside the Animals grid — the lot
 * picker (`MyStore/CreateLotModal`) and the racks "Who's inside" drawer
 * (`Racks/rackHelpers.formatAnimalDisplay`, desktop and phone) both read
 * it, so the two cannot describe one animal differently.
 *
 *   handle — its NAME, else "#<first six of its id>" (the short id the
 *            grid, the lot chips and the storefront already print;
 *            `CreateLotModal.memberDisplayName` delegates here);
 *   sex    — `sexDisplay`: the WORD with its glyph, never a glyph alone;
 *   kind   — the species' common name, its locality, its morph;
 *   where  — "in Tadpole 2E — Rack 2 · 2.3" through `animalPlaceText`, or
 *            "not in a tank".
 */
export const SHORT_ID_LENGTH = 6;

export const animalHandle = (animal) => {
  const name = String(animal?.name || '').trim();
  if (name) return name;
  return `#${String(animal?.id || '').slice(0, SHORT_ID_LENGTH).toUpperCase()}`;
};

/** What an animal in no tank says about where it is. */
export const NOT_IN_A_TANK = 'not in a tank';

/** Is the animal in a tank at all? The ONE reading of it (the place text
 *  below and the R5 identity line both ask). */
export const animalHasTank = (animal) => Boolean(
  animal?.enclosures_id || animal?.enclosure_id || animal?.enclosure_name,
);

/** What a tank with no name of its own is called. */
export const UNNAMED_TANK = 'an unnamed tank';

/**
 * THE PIECES OF WHERE AN ANIMAL LIVES — the ONE derivation the place text
 * below and the R5 identity line (`utils/identityLine.js`) both read, so
 * the two can never say a different rack or slot:
 *   null for an animal in no tank (the caller says `NOT_IN_A_TANK`), else
 *   { tank: the tank's own name or '',
 *     rackSlot: "Rack 2 · 2.3" through `rackAndSlot` (never a slot on its
 *               own), the rack alone when the slot is unknown, or '' for a
 *               tank on no rack }.
 */
export const animalPlaceParts = (animal) => {
  if (!animalHasTank(animal)) return null;
  const tank = String(animal?.enclosure_name || '').trim();
  const rackName = String(animal?.rack_name || '').trim();
  const rackSlot = rackName
    ? (rackAndSlot({ rackName, positionLabel: animal?.enclosure_position_label })
      || rackName)
    : '';
  return { tank, rackSlot };
};

/**
 * WHERE THE ANIMAL LIVES — "Tadpole 2E — Rack 2 · 2.3", the Animals
 * grid's own Where shape (`enclosureCellText`), with the rack beside the
 * slot through `rackAndSlot` (never a slot on its own). A tank on no rack
 * is its tank's name; a tank with no name says "an unnamed tank" plus its
 * place; an animal in no tank answers null (the caller says
 * `NOT_IN_A_TANK`).
 */
export const animalPlaceText = (animal) => {
  const parts = animalPlaceParts(animal);
  if (!parts) return null;
  const { tank, rackSlot } = parts;
  if (tank && rackSlot) return `${tank} \u2014 ${rackSlot}`;
  if (rackSlot) return `${UNNAMED_TANK} \u2014 ${rackSlot}`;
  return tank || UNNAMED_TANK;
};

/**
 * The two halves of `animalKindText`, exported so the R5 identity line
 * (`utils/identityLine.js`, "Species, Morph") joins the SAME pieces its
 * own way instead of re-deriving them. `animalKindText`'s output is
 * unchanged.
 *
 *   animalSpeciesText -> 'Dyeing Dart Frog "Azureus"' (common name plus
 *                        locality; the scientific name when no common one)
 *   animalMorphText   -> 'Cobalt', or ''
 */
export const animalSpeciesText = (animal) => {
  const sp = animal?.species && typeof animal.species === 'object' ? animal.species : {};
  const locality = String(sp.locality || animal?.species_locality || '').trim();
  const flatName = String(animal?.species_name || '').trim();
  const common = flatName || getAnimalCommonName(animal);
  if (common && common !== 'Unknown') {
    return locality ? `${common} ${formatLocality(locality, sp.species_type)}` : common;
  }
  return scientificName(animal);
};

export const animalMorphText = (animal) => String(
  animal?.morph_name || animal?.genetics?.expressed_morph?.name || '',
).trim();

/** 'Dyeing Dart Frog "Azureus" — Cobalt' — species, locality, morph. */
export const animalKindText = (animal) => {
  const base = animalSpeciesText(animal);
  const morph = animalMorphText(animal);
  return [base, morph].filter(Boolean).join(' \u2014 ');
};

export const animalIdentity = (animal) => {
  const handle = animalHandle(animal);
  const sex = sexDisplay(animal?.gender, animal?.sex_basis);
  const kind = animalKindText(animal);
  const place = animalPlaceText(animal);
  return {
    handle,
    sex,
    kind,
    place,
    where: place ? `in ${place}` : NOT_IN_A_TANK,
  };
};

/**
 * I-02b.2 — listing state: WHY an animal is/isn't live. Actionable gaps
 * (needs photo / needs price) show red; Listed green; everything else
 * neutral. Third element: the hover explanation.
 */
export const LISTING_DISPLAY = {
  // Cowork B8: Listed is a SUCCESS state → green (-yes token), not the
  // blue "available" token.
  listed: ['Listed', 'animals-status-yes', 'Live on the storefront'],
  no_photo: ['Needs photo', 'animals-status-actionable', 'Not listed — needs its own photo (Set Image or Manage Products → Animals panel)'],
  no_price: ['Needs price', 'animals-status-actionable', 'Not listed — needs a price override or a variation price'],
  holdback: ['Holdback', 'animals-status-no', 'Not listed — Holdback flag is on'],
  breeder: ['Breeder', 'animals-status-no', 'Not listed — Breeder flag is on (breeding animals never list)'],
  not_for_sale: ['Not for sale', 'animals-status-no', 'Not listed — For Sale is off on this animal'],
  // I-03 close 1c: the species is set "Not sold here" (kept, never sold).
  // Neutral, not red: nothing for the operator to fix on the animal.
  not_sold_here: ['Not sold here', 'animals-status-no', 'Not listed — this species is set to Not sold here (care guide only)'],
  stock_mode: ['Stock mode', 'animals-status-no', 'Only linked to stock-mode variations'],
  listing_off: ['Listing off', 'animals-status-no', 'Not listed — its variation is not for sale (or product archived)'],
  // Store-tiers review rule 3 (2026-10-18): a listed reptile with no hatch
  // date has no sellable age — every availability count leaves it out.
  missing_hatch_date: ['Missing hatch date', 'animals-status-actionable', 'Missing hatch date — not shown to customers until one is set'],
  // FIX 2 — the orphan state. The backend only reaches 'not_linked' when
  // is_for_sale is ON (every other case returns earlier), so this ALWAYS
  // means "marked for sale, sells nowhere". That's an actionable gap, not
  // a neutral one — red, like Needs photo / Needs price. Re-running Mark
  // for Sale will NOT fix it: mark_as_products rejects animals that are
  // already is_for_sale.
  not_linked: ['No listing', 'animals-status-actionable', 'Marked for sale but attached to no listing — it sells nowhere. Fix: Edit Details → turn For Sale off, save, then re-select it and use Mark for Sale to attach it.'],
  // I-02d — active-lot members sell as their lot; the backend emits this
  // and the grid had no entry, so it rendered a bare "—" with no
  // explanation.
  in_lot: ['In lot', 'animals-status-available', 'Sells as part of an active lot — manage it in Manage Products → Lots'],
  sold: ['Sold', 'animals-status-no', 'Already sold'],
  on_hold: ['On hold', 'animals-status-no', 'On hold for an order'],
  deceased: ['—', 'animals-status-no', 'Deceased'],
};

/** `[label, className, tooltip]` for a row's `listing_status`. */
export const listingDisplay = (status) => (
  LISTING_DISPLAY[status] || ['—', 'animals-status-no', '']
);

/**
 * THE STATUS CHIPS, IN THE OPERATOR'S ORDER — possession first, then the
 * commercial states, then the two never-list flags. It is a fixed order
 * on purpose: an operator scanning twelve rows reads the same position
 * for the same fact every time, which is the whole reason a row of
 * chips beats a row of Yes/No columns.
 *
 * `tone` maps to the `animals-status-*` tokens `animals.css` owns, so a
 * clickable state and a read-only one are the same colour for the same
 * meaning.
 */
export const STATUS_FLAGS = [
  { field: 'is_in_possession', label: 'In possession', tone: 'yes' },
  { field: 'is_for_sale', label: 'For sale', tone: 'yes' },
  { field: 'is_on_hold', label: 'On hold', tone: 'yes' },
  { field: 'is_sold', label: 'Sold', tone: 'sold' },
  { field: 'is_deceased', label: 'Deceased', tone: 'deceased' },
  { field: 'hold_back', label: 'Holdback', tone: 'available' },
  { field: 'breeder', label: 'Breeder', tone: 'available' },
];

/**
 * ONE CONSEQUENCE, ONE WORDING, ONE TONE (fix pass 4).
 *
 * ⚠️ The three un-listing flags said "It comes off sale." and
 * `forSaleConfirm` — the SAME act, asked from the For-sale chip — said
 * "It leaves the storefront." in the danger tone. Four popovers described
 * one consequence in two registers, and the operator was left to work out
 * whether they were the same thing. They are: the animal stops being a
 * listing. Both halves are shared, so the next flag that takes an animal
 * off the storefront cannot invent a third wording.
 */
export const UNLISTS_THE_ANIMAL = 'It leaves the storefront.';
export const UNLISTS_TONE = 'danger';

/**
 * The popover a flag needs before it is turned ON, in WORDS — naming
 * every companion flag `_normalize_animal_flags` will clear. Turning
 * either of these OFF clears nothing and is immediate.
 *
 * ⚠️ `is_for_sale` is the other way round: it may only be turned OFF
 * here (the server answers 400 `for_sale_not_permitted_here` to on), so
 * its question is asked on the way DOWN. See `forSaleConfirm`.
 *
 * ⚠️ AND THREE MORE FLAGS TAKE AN ANIMAL OFF THE STOREFRONT (fix pass 3,
 * SHOULD 2). `_normalize_animal_flags` Rules 4 and 5 clear `is_for_sale`
 * for **On hold**, **Holdback** and **Breeder** — so a single click on any
 * of them de-listed a LISTED animal with nothing said about it, while
 * taking that same animal off sale by its own chip asks first
 * (`forSaleConfirm`). One act, two answers, and the quiet one was the one
 * nobody would go looking for.
 *
 * `forSale` is why the question is asked at all: on an animal that is NOT
 * listed these three clear nothing, so they stay immediate — a confirm
 * for a consequence that cannot happen is a click the operator learns to
 * dismiss without reading.
 *
 * The three un-listing popovers and `forSaleConfirm` all say
 * `UNLISTS_THE_ANIMAL` in `UNLISTS_TONE` — see the constants above.
 */
export const turnOnConfirm = (field, name, { forSale = false } = {}) => {
  const who = name || 'this animal';
  if (field === 'is_deceased') {
    return {
      title: `Mark ${who} deceased?`,
      body: 'This also clears In possession, For sale, On hold and Sold.',
      confirmLabel: 'Mark deceased',
      tone: 'danger',
    };
  }
  if (field === 'is_sold') {
    return {
      title: `Mark ${who} sold?`,
      body: 'This clears For sale and On hold.',
      confirmLabel: 'Mark sold',
    };
  }
  if (!forSale) return null;
  if (field === 'is_on_hold') {
    return {
      title: `Put ${who} on hold?`,
      body: UNLISTS_THE_ANIMAL,
      confirmLabel: 'Put on hold',
      tone: UNLISTS_TONE,
    };
  }
  if (field === 'hold_back') {
    return {
      title: `Mark ${who} holdback?`,
      body: UNLISTS_THE_ANIMAL,
      confirmLabel: 'Mark holdback',
      tone: UNLISTS_TONE,
    };
  }
  if (field === 'breeder') {
    return {
      title: `Mark ${who} a breeder?`,
      body: UNLISTS_THE_ANIMAL,
      confirmLabel: 'Mark breeder',
      tone: UNLISTS_TONE,
    };
  }
  return null;
};

/** Taking a listed animal off sale is a storefront change — ask first. */
export const forSaleConfirm = (name) => ({
  title: `Take ${name || 'this animal'} off sale?`,
  body: UNLISTS_THE_ANIMAL,
  confirmLabel: 'Take off sale',
  tone: UNLISTS_TONE,
});

/** The modal's own words, kept verbatim — it is the operator's map. */
export const FOR_SALE_OFF_REASON = 'Use Mark for Sale to list this animal';

// ── sorting (§6) ──────────────────────────────────────────────────────
//
// CLIENT-SIDE, because `list_animals` returns the WHOLE server-filtered
// set as a flat array (it paginates only when explicitly asked to) and
// the grid's paging is already client-side. Sorting on the server would
// re-fetch a list we already hold.
//
// Every key answers a value the CELL shows, so the order on screen and
// the order in the array are the same question. A missing value sorts
// LAST rather than first: an animal with no price is not the cheapest
// one.
//
// ⚠️ AND "LAST" MEANS LAST IN BOTH DIRECTIONS, WHICH A SENTINEL VALUE
// CANNOT DELIVER (S3-05.1 review round 2). The first cut said "missing"
// by RETURNING a value the comparator would rank high — `'￿'` for the
// text keys, `POSITIVE_INFINITY` for age and price — and the comparator
// then multiplied that ranking by the DIRECTION. So the fix worked
// ascending and inverted descending: one click on Price put the priced
// animals in order with the dashes at the bottom, a second click put a
// page of dashes back on top. The sentinel is not the fix, it is the
// same bug wearing a different sign.
//
// So a key answers `[isMissing, value]` — the FLAG FIRST — and the
// comparator ranks the flag WITHOUT the sign and the value WITH it. One
// place, every key, both directions.
/** "There is nothing to sort by here." The value is never compared. */
const MISSING = [1, null];
/** A value there is. */
const present = (value) => [0, value];

/** A trimmed, lower-cased string, or MISSING for an empty one. */
const textOrMissing = (value) => {
  const s = value == null ? '' : String(value).trim().toLowerCase();
  return s ? present(s) : MISSING;
};

/**
 * A number, or MISSING for anything that is not one — a null, an empty
 * string and an unparseable "N/A" are all "no value", which is a
 * different answer from zero.
 */
const numberOrMissing = (value) => {
  if (value == null || value === '') return MISSING;
  const n = Number.parseFloat(value);
  return Number.isNaN(n) ? MISSING : present(n);
};

export const SORT_KEYS = {
  name: (a) => textOrMissing(a.name),
  species: (a) => textOrMissing(scientificName(a)),
  morph: (a) => textOrMissing(a.genetics?.expressed_morph?.name),
  sex: (a) => (a.gender == null ? MISSING : present(Number(a.gender))),
  // By `age_days`, never by the DISPLAY string: "14 mo" and "9 wk" do not
  // compare as text.
  age: (a) => numberOrMissing(a.age_days),
  where: (a) => (a.enclosure_name
    ? textOrMissing(enclosureCellText(a)) : MISSING),
  price: (a) => numberOrMissing(a.effective_price),
};

/**
 * A new list, sorted. `key: null` means "the server's own order", which
 * is the default: until a header is clicked the grid shows what the
 * endpoint sent.
 *
 * The tiebreak is the ID, so equal values keep ONE order across renders
 * — without it `?animal=`'s `findIndex` could page to a different index
 * than the one the row is drawn at. Two MISSING rows are equal by
 * definition and fall to it.
 */
export const sortAnimals = (animals, key, dir) => {
  const read = key && SORT_KEYS[key];
  if (!read) return animals;
  const sign = dir === 'desc' ? -1 : 1;
  return [...animals].sort((a, b) => {
    const [aMissing, av] = read(a);
    const [bMissing, bv] = read(b);
    // THE FLAG IS NOT SIGNED. A row with nothing to sort by loses to a
    // row that has something, ascending and descending alike.
    if (aMissing !== bMissing) return aMissing - bMissing;
    if (!aMissing) {
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
    }
    return String(a.id).localeCompare(String(b.id));
  });
};


/**
 * The sentences of a response's `warnings`, as one line (Cowork run 26,
 * F2). The server's warning is `{code, message}`; only the MESSAGE is for
 * the keeper — a code is never printed.
 */
/** The notice key for one animal's role warning (Cowork run 29): a later
 *  sex change on that animal that answers no warning takes it down. */
export const roleWarningKey = (animalId) => `animal-role-warning:${animalId}`;

export const warningSentences = (warnings) => (Array.isArray(warnings) ? warnings : [])
  .map((row) => (row && typeof row === 'object' ? row.message : row))
  .filter((said) => typeof said === 'string' && said.trim())
  .join(' ');

/**
 * A response's `warnings`, said ONCE per repeated fact (Cowork run 28, D8).
 *
 * A sire made female that is the sire on ten pairings and one hatchling
 * answered eleven near-identical sentences, and the notice printed all of
 * them in one block. The server's `role_contradicts_sex` rows carry their
 * own parts (`animal_name`, `role`, `sex`, and `pairing_id` or `link_id`),
 * so two or more rows about the same animal, role and sex fold into ONE
 * summary line, and the server's own sentences ride behind it as
 * `details` — every one still reachable, none reworded.
 *
 * `{summary, details}`: `summary` is the line to show (the folded lines
 * and every other message, in order); `details` is the server sentences
 * that were folded (empty when nothing was). A row without its parts — an
 * older API, any other code — is shown as it was sent.
 */
export const summariseWarnings = (warnings) => {
  const rows = (Array.isArray(warnings) ? warnings : [])
    .map((row) => (row && typeof row === 'object' ? row : { message: row }))
    .filter((row) => typeof row.message === 'string' && row.message.trim());
  const keyOf = (row) => (row.code === 'role_contradicts_sex'
    && row.animal_name && row.role && row.sex
    ? `${row.animal_name}\u0000${row.role}\u0000${row.sex}` : null);
  const groups = new Map();
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key) groups.set(key, [...(groups.get(key) || []), row]);
  });
  const lines = [];
  const details = [];
  const said = new Set();
  rows.forEach((row) => {
    const key = keyOf(row);
    const group = key ? groups.get(key) : null;
    if (!group || group.length < 2) {
      lines.push(row.message.trim());
      return;
    }
    if (said.has(key)) return;
    said.add(key);
    const pairings = group.filter((r) => r.pairing_id).length;
    const children = group.length - pairings;
    const fix = [
      pairings ? 'fix the role on each pairing' : '',
      children ? (children === 1 ? "change the child's parents"
        : "change each child's parents") : '',
    ].filter(Boolean).join(' and ');
    lines.push(`${row.animal_name} is recorded as the ${row.role} on `
      + `${group.length} records and is now ${row.sex} — ${fix}.`);
    group.forEach((r) => details.push(r.message.trim()));
  });
  return { summary: lines.join(' '), details };
};
